import { HangoutState } from '../../state/HangoutState.js';
import { HangoutService } from '../../services/HangoutService.js';
import { populateVoiceSelect, getVoiceById, isElevenLabsProviderVoiceId } from '../../../services/voiceService.js';
import { fetchVoicePreview, randomPreviewIndex } from '../../../services/voicePreview.js';
import { fetchCharacterSpriteUrl } from '../character-manager.js';
import { CDN_URL, WORKER_URL, USER_WORKER_URL } from '../../../config.js';
import { withNetworkRetry } from '../../../utils/error-handling.js';
import { fetchCache } from '../../../utils/fetchCache.js';
import { getSession } from '../../../utils/auth_utils.js';
import { fetchPrivateCharacterById } from '../../../utils/privateCharactersApi.js';
import { normalizeGender } from '../../../utils/genderUtils.js';
import { getVoiceLanguageCodesForProvider } from '../../../utils/languageData.js';
import { pickStandingActionId } from '../../../utils/spriteFallbacks.js';
import { applySfwQueryParam } from '../../../utils/nsfwTags.js';
import { storage } from '../../../utils/storage.js';
import { storageManager } from '../../../../core/storage/index.js';
import {
  fetchCharacterVideos,
  getVideoBadgeText,
  buildVideoByActionId,
  hasExistingVideo,
  bindSpriteGalleryNameFilter
} from '../../../utils/spriteVideoStatus.js';
import {
  getActionCap,
  getTruncatedSelectionKeys,
  formatSpriteGalleryStatus,
  isSpriteSelectionOverCap
} from '../../../utils/sprite-gallery-limits.js';
import { getSpriteSmallImagePath, formatSpriteNameForDisplay } from '../../../create/utils/spriteDisplay.js';
import { getUserData, hasPaidMembership } from './settings-user-utils.js';
import { getCurrentLanguage, applyTranslationsToContainer } from '../../../translations.js';
import { createFlagElement } from '../../../../components/navigation/Navigation.js';
import { getVibeOptions, getUserVibe, getUserModelOverride, setUserModelOverride, lookupOpenRouterTextModel, getResponseStylePriceAnchor, getUserReasoningEffort, getUserTemperature, getUserMaxTokens, getUserContextTurns, getUserTopP, getUserFrequencyPenalty, getUserPresencePenalty, getStyleAccessLane, isStyleAllowedForMembership, accessLaneFromMultiplier, isMultiplierAllowedForMembership, modelLaneSkipped, REASONING_EFFORTS, DEFAULT_REASONING_EFFORT, DEFAULT_TEMPERATURE, DEFAULT_MAX_TOKENS, DEFAULT_CONTEXT_TURNS, CUSTOM_PROMPT_MAX_LENGTH, CUSTOM_PROMPT_NAME_MAX_LENGTH } from '../constants.js';
import { creditMultiplierFromPricing, openRouterPricingPerMillion } from '../model-price.js';
import { createCreditMultiplierBadge } from '../../../utils/creditMultiplier.js';
import { showModelAccessRequiredModal } from '../../../utils/quotaErrorHandler.js';
import { invalidateCachedVoiceForCharacter } from '../voice-utils.js';
import { estimateTokens, formatTokenCount } from '../token-estimate.js';
import { createCustomPrompt, fetchCustomPrompts, schedulePersistResponseStyle } from '../hangout-settings-persist.js';
import { showToast } from '../../../utils/toast.js';
import { attachSpriteContextMenuListeners } from '../SpriteContextMenu.js';
import {
  getSpriteSelectionOptions,
  setSpriteSelectionOption,
  getHideSmartUnavailable,
  setHideSmartUnavailable,
  SPRITE_PREVENT_REPETITION_KEY,
  SPRITE_USE_SMART_SELECTION_KEY
} from '../request-builder.js';
import {
  ACTION_FAMILIES,
  classifyActionFamily,
  applySmartFamilyFilter
} from '../action-families.js';

// Import extracted modules
import { createCharacterTabs } from '../components/character-tabs.js';
import { appendCharacterInfoFields, applyCharacterInfoDynamicTranslations } from './character-info-dynamic-fields.js';
import {
  createVoiceLanguageControl,
  syncFixedVoiceLanguageControl,
  LANGUAGE_TO_FLAG,
  LANGUAGE_TO_NAME
} from '../components/voice-language-ui.js';

/**
 * Resolves the localized label for a reasoning effort, falling back to a capitalized id.
 * @param {string} effort
 * @returns {string}
 */
function reasoningEffortLabel(effort) {
  const lang = getCurrentLanguage();
  const fallback = effort.charAt(0).toUpperCase() + effort.slice(1);
  return window.translations?.[lang]?.hangout?.character_modal?.[`reasoning_${effort}`] ?? fallback;
}

/**
 * Builds a single vibe card element from a vibe option.
 * @param {object} vibe
 * @param {boolean} isSelected
 * @param {{ locked?: boolean, accessLane?: 'included' | 'plus' | 'max' }} [options]
 * @returns {HTMLElement}
 */
function buildVibeCard(vibe, isSelected, { locked = false, accessLane = 'included' } = {}) {
  const card = document.createElement('div');
  card.className = 'vibe-card';
  if (isSelected) card.classList.add('selected');
  if (locked) {
    card.classList.add('is-locked');
    card.setAttribute('aria-disabled', 'true');
  }
  card.setAttribute('data-vibe-id', String(vibe.id));
  card.setAttribute('role', 'button');
  card.setAttribute('tabindex', '0');
  const lockedAriaFallback = accessLane === 'max'
    ? `${vibe.name}, Max model, included with Premium`
    : `${vibe.name}, Plus model, included with Starter`;
  card.setAttribute('aria-label', locked ? lockedAriaFallback : vibe.name);

  const isNSFW = Boolean(vibe.is_nsfw);
  const costBadge = createCreditMultiplierBadge(vibe.price, { hideWhenOne: false, variant: 'chat' });
  const costBadgeHtml = costBadge ? costBadge.outerHTML : '';
  const lockHtml = locked
    ? '<span class="vibe-lock-icon" aria-hidden="true"><i class="fas fa-lock"></i></span>'
    : '';
  let laneTag = '';
  if (accessLane === 'plus') {
    laneTag = '<span class="vibe-tag plus" data-translate="character_modal.vibe_tag_plus">Plus</span>';
  } else if (accessLane === 'max') {
    laneTag = '<span class="vibe-tag max" data-translate="character_modal.vibe_tag_max">Max</span>';
  }

  const formatCtx = (n) => {
    if (n >= 1048576) return Math.round(n / 1048576) + 'M';
    if (n >= 1024) return Math.round(n / 1024) + 'K';
    return String(n);
  };

  const ctxTag = vibe.context_length != null
    ? `<span class="vibe-tag context" data-translate-attr="title:character_modal.vibe_context_tooltip" title="">${formatCtx(vibe.context_length)} <span data-translate="character_modal.vibe_tag_context"></span></span>` : '';
  const speedTag = (vibe.speed === 4 || vibe.speed === 5)
    ? '<span class="vibe-tag speed" data-translate="character_modal.vibe_tag_fast_responses"></span>' : '';

  card.innerHTML = `
      <div class="vibe-card-header">
        <div class="vibe-radio-indicator"></div>
        <span class="vibe-name">${vibe.name}</span>
        ${lockHtml}
        <div class="vibe-tags-row">${isNSFW ? '<span class="vibe-tag nsfw">NSFW</span>' : ''}${laneTag}${costBadgeHtml}${ctxTag}${speedTag}</div>
      </div>
      <div class="vibe-description">${vibe.description}</div>
    `;
  return card;
}

/**
 * Builds an override slider row (label, info button, live value, range input, captions).
 * @param {{labelKey: string, labelText: string, infoKey: string, infoText: string,
 *          min: number, max: number, step: number, value: number,
 *          minLabelKey: string, minLabelText: string, maxLabelKey: string, maxLabelText: string,
 *          formatValue: (v: number) => string, onInput: (v: number) => void,
 *          isReasoning?: boolean}} config
 * @returns {{ row: HTMLElement, input: HTMLInputElement, valueLabel: HTMLElement, setValue: (v: number, persist?: boolean) => void }}
 */
function createOverrideSlider({ labelKey, labelText, infoKey, infoText, min, max, step, value, minLabelKey, minLabelText, maxLabelKey, maxLabelText, formatValue, onInput, isReasoning = false }) {
  const row = document.createElement('div');
  row.className = 'vibe-override-row';

  const head = document.createElement('div');
  head.className = 'vibe-override-head';

  const labelWrap = document.createElement('div');
  labelWrap.className = 'vibe-override-label-wrap';

  const label = document.createElement('span');
  label.className = 'vibe-override-label';
  label.textContent = labelText;
  label.setAttribute('data-translate', labelKey);

  const infoBtn = document.createElement('button');
  infoBtn.type = 'button';
  infoBtn.className = 'vibe-info-btn';
  infoBtn.textContent = 'i';
  infoBtn.setAttribute('aria-label', infoText);
  infoBtn.setAttribute('data-translate-attr', `aria-label:${infoKey}`);

  labelWrap.appendChild(label);
  labelWrap.appendChild(infoBtn);

  const valueLabel = document.createElement('span');
  valueLabel.className = 'vibe-override-value';
  valueLabel.textContent = formatValue(value);

  head.appendChild(labelWrap);
  head.appendChild(valueLabel);

  const input = document.createElement('input');
  input.type = 'range';
  input.className = 'vibe-override-slider';
  input.min = String(min);
  input.max = String(max);
  input.step = String(step);
  input.value = String(value);

  // WebKit/Blink: paint the portion left of the thumb with --pink-glow (Firefox uses ::-moz-range-progress).
  const updateFill = () => {
    const pct = max === min ? 0 : ((parseFloat(input.value) - min) / (max - min)) * 100;
    input.style.background = `linear-gradient(to right, var(--pink-glow) 0% ${pct}%, rgba(255,255,255,0.15) ${pct}% 100%)`;
  };
  updateFill();

  const applyDisplayedValue = (current) => {
    input.value = String(current);
    valueLabel.textContent = formatValue(current);
    updateFill();
  };

  input.addEventListener('input', () => {
    const current = parseFloat(input.value);
    applyDisplayedValue(current);
    onInput(current);
  });

  const captions = document.createElement('div');
  captions.className = 'vibe-slider-captions';
  const minCaption = document.createElement('span');
  minCaption.className = 'vibe-slider-caption';
  minCaption.textContent = minLabelText;
  minCaption.setAttribute('data-translate', minLabelKey);
  const maxCaption = document.createElement('span');
  maxCaption.className = 'vibe-slider-caption';
  maxCaption.textContent = maxLabelText;
  maxCaption.setAttribute('data-translate', maxLabelKey);
  captions.appendChild(minCaption);
  captions.appendChild(maxCaption);

  const infoTooltip = document.createElement('div');
  infoTooltip.className = 'vibe-info-tooltip';
  infoTooltip.textContent = infoText;
  infoTooltip.setAttribute('data-translate', infoKey);

  infoBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    row.classList.toggle('info-visible');
  });

  row.appendChild(head);
  row.appendChild(input);
  row.appendChild(captions);
  row.appendChild(infoTooltip);

  if (isReasoning) {
    const tooltip = document.createElement('div');
    tooltip.className = 'vibe-override-tooltip';
    tooltip.setAttribute('data-translate', 'character_modal.reasoning_unavailable_tooltip');
    tooltip.textContent = 'This response style does not support reasoning.';
    row.appendChild(tooltip);

    // Mobile: tapping the disabled slider toggles the tooltip (hover handles desktop).
    row.addEventListener('pointerdown', () => {
      if (!row.classList.contains('is-disabled')) return;
      row.classList.toggle('tooltip-visible');
    });
  }

  const setValue = (next, persist = true) => {
    const current = parseFloat(String(next));
    if (!Number.isFinite(current)) return;
    applyDisplayedValue(current);
    if (persist) onInput(current);
  };

  return { row, input, valueLabel, setValue };
}

const getCharacterModalText = (key, fallback = '') => {
  const lang = getCurrentLanguage();
  return window.translations?.[lang]?.hangout?.character_modal?.[key]
    ?? window.translations?.en?.hangout?.character_modal?.[key]
    ?? fallback;
};

const createCustomPromptPicker = ({ onSelectionChange } = {}) => {
  const notifySelection = () => {
    if (typeof onSelectionChange === 'function') onSelectionChange();
  };
  const wrap = document.createElement('div');
  wrap.className = 'vibe-override-row vibe-custom-prompt-group';

  const head = document.createElement('div');
  head.className = 'vibe-override-head';
  const labelWrap = document.createElement('div');
  labelWrap.className = 'vibe-override-label-wrap';
  const label = document.createElement('span');
  label.className = 'vibe-override-label';
  label.textContent = 'Custom prompt';
  label.setAttribute('data-translate', 'character_modal.custom_prompt');
  const infoText = 'Adds extra instructions to every text reply. It gets added right after our own prompt.';
  const infoBtn = document.createElement('button');
  infoBtn.type = 'button';
  infoBtn.className = 'vibe-info-btn';
  infoBtn.textContent = 'i';
  infoBtn.setAttribute('aria-label', infoText);
  infoBtn.setAttribute('data-translate-attr', 'aria-label:character_modal.custom_prompt_info');
  infoBtn.addEventListener('click', (event) => {
    event.stopPropagation();
    wrap.classList.toggle('info-visible');
  });
  labelWrap.appendChild(label);
  labelWrap.appendChild(infoBtn);
  head.appendChild(labelWrap);
  wrap.appendChild(head);

  const infoTooltip = document.createElement('div');
  infoTooltip.className = 'vibe-info-tooltip';
  infoTooltip.textContent = infoText;
  infoTooltip.setAttribute('data-translate', 'character_modal.custom_prompt_info');
  head.appendChild(infoTooltip);

  const cardsContainer = document.createElement('div');
  cardsContainer.className = 'vibe-cards-container custom-prompt-cards is-collapsed';

  const expandBtn = document.createElement('button');
  expandBtn.type = 'button';
  expandBtn.className = 'vibe-expand-btn';
  expandBtn.setAttribute('aria-label', 'Show all custom prompts');
  expandBtn.setAttribute('aria-expanded', 'false');
  expandBtn.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" d="M9 6l6 6-6 6"/></svg>';

  const setExpanded = (expanded) => {
    cardsContainer.classList.toggle('is-collapsed', !expanded);
    expandBtn.setAttribute('aria-expanded', String(expanded));
  };

  const getSelectedId = () => {
    const raw = storage.get('selectedCustomPromptId');
    if (raw == null || raw === '') return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  };

  let savedPrompts = [];
  let promptsLoaded = false;
  let selectedMode = getSelectedId() == null ? 'none' : 'saved';

  const noneCard = document.createElement('div');
  noneCard.className = 'vibe-card custom-prompt-card';
  noneCard.innerHTML = `
    <div class="vibe-card-header">
      <div class="vibe-radio-indicator"></div>
      <span class="vibe-name" data-translate="character_modal.custom_prompt_none">None</span>
    </div>
  `;

  const createCard = document.createElement('div');
  createCard.className = 'vibe-card custom-prompt-card custom-prompt-create-card';
  const createHeader = document.createElement('div');
  createHeader.className = 'vibe-card-header';
  createHeader.innerHTML = `
    <div class="vibe-radio-indicator"></div>
    <span class="vibe-name" data-translate="character_modal.custom_prompt_create">Create new</span>
  `;
  const createFields = document.createElement('div');
  createFields.className = 'custom-prompt-create-fields';
  const nameInput = document.createElement('input');
  nameInput.type = 'text';
  nameInput.className = 'value';
  nameInput.maxLength = CUSTOM_PROMPT_NAME_MAX_LENGTH;
  nameInput.setAttribute('data-translate-placeholder', 'character_modal.custom_prompt_name_placeholder');
  nameInput.placeholder = 'Prompt name';
  nameInput.setAttribute('aria-label', 'Custom prompt name');
  const promptTextarea = document.createElement('textarea');
  promptTextarea.className = 'vibe-custom-prompt';
  promptTextarea.maxLength = CUSTOM_PROMPT_MAX_LENGTH;
  promptTextarea.rows = 4;
  promptTextarea.setAttribute('data-translate-placeholder', 'character_modal.custom_prompt_placeholder');
  promptTextarea.placeholder = 'Extra instructions for the text response';
  const tokenEl = document.createElement('div');
  tokenEl.className = 'chat-memory-token-count';
  const updateTokenCount = () => {
    tokenEl.textContent = formatTokenCount(estimateTokens(promptTextarea.value));
  };
  promptTextarea.addEventListener('input', updateTokenCount);
  updateTokenCount();
  const saveBtn = document.createElement('button');
  saveBtn.type = 'button';
  saveBtn.className = 'selection-btn';
  saveBtn.setAttribute('data-translate', 'character_modal.custom_prompt_save');
  saveBtn.textContent = 'Save';
  createFields.appendChild(nameInput);
  createFields.appendChild(promptTextarea);
  createFields.appendChild(tokenEl);
  createFields.appendChild(saveBtn);
  createCard.appendChild(createHeader);
  createCard.appendChild(createFields);

  const markSelected = (card) => {
    cardsContainer.querySelectorAll('.vibe-card').forEach((el) => el.classList.remove('selected'));
    card.classList.add('selected');
    card.appendChild(expandBtn);
  };

  const selectNone = () => {
    selectedMode = 'none';
    storage.remove('selectedCustomPromptId');
    storage.remove('selectedCustomPromptName');
    storage.set('selectedCustomPrompt', '');
    markSelected(noneCard);
    setExpanded(false);
    notifySelection();
  };

  const selectSaved = (entry) => {
    selectedMode = 'saved';
    storage.set('selectedCustomPromptId', entry.id);
    storage.set('selectedCustomPromptName', entry.name || '');
    storage.set('selectedCustomPrompt', String(entry.prompt || '').slice(0, CUSTOM_PROMPT_MAX_LENGTH));
    const card = cardsContainer.querySelector(`[data-prompt-id="${entry.id}"]`);
    if (card) markSelected(card);
    setExpanded(false);
    notifySelection();
  };

  const selectCreate = () => {
    selectedMode = 'create';
    markSelected(createCard);
  };

  const rebuildSavedCards = () => {
    cardsContainer.querySelectorAll('[data-prompt-id]').forEach((el) => el.remove());
    const selectedId = getSelectedId();
    savedPrompts.forEach((entry) => {
      const card = document.createElement('div');
      card.className = 'vibe-card custom-prompt-card';
      card.setAttribute('data-prompt-id', String(entry.id));
      card.innerHTML = `
        <div class="vibe-card-header">
          <div class="vibe-radio-indicator"></div>
          <span class="vibe-name"></span>
        </div>
      `;
      card.querySelector('.vibe-name').textContent = entry.name;
      card.addEventListener('click', () => {
        if (cardsContainer.classList.contains('is-collapsed')) return;
        selectSaved(entry);
      });
      cardsContainer.insertBefore(card, createCard);
      if (selectedMode === 'saved' && selectedId === Number(entry.id)) markSelected(card);
    });
  };

  noneCard.addEventListener('click', () => {
    if (cardsContainer.classList.contains('is-collapsed')) return;
    selectNone();
  });
  createCard.addEventListener('click', (event) => {
    if (event.target.closest('input, textarea, button')) return;
    if (cardsContainer.classList.contains('is-collapsed')) return;
    selectCreate();
  });
  saveBtn.addEventListener('click', async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const name = nameInput.value.trim().slice(0, CUSTOM_PROMPT_NAME_MAX_LENGTH);
    const prompt = promptTextarea.value.trim().slice(0, CUSTOM_PROMPT_MAX_LENGTH);
    if (!name || !prompt) {
      showToast(getCharacterModalText('custom_prompt_save_required', 'Enter a name and prompt'), 'exclamation-triangle');
      return;
    }
    saveBtn.disabled = true;
    try {
      const created = await createCustomPrompt(name, prompt);
      if (!created?.id) throw new Error('No prompt returned');
      savedPrompts = [...savedPrompts.filter((item) => Number(item.id) !== Number(created.id)), created].slice(0, 20);
      rebuildSavedCards();
      nameInput.value = '';
      promptTextarea.value = '';
      updateTokenCount();
      selectSaved(created);
    } catch (error) {
      showToast(error.message || getCharacterModalText('custom_prompt_save_failed', 'Failed to save prompt'), 'exclamation-triangle');
    } finally {
      saveBtn.disabled = false;
    }
  });

  expandBtn.addEventListener('click', async (event) => {
    event.stopPropagation();
    const willExpand = cardsContainer.classList.contains('is-collapsed');
    setExpanded(willExpand);
    if (!willExpand) return;
    savedPrompts = await fetchCustomPrompts();
    promptsLoaded = true;
    rebuildSavedCards();
    applyTranslationsToContainer(wrap, 'hangout', getCurrentLanguage());
    if (selectedMode === 'none') markSelected(noneCard);
    else if (selectedMode === 'create') markSelected(createCard);
  });

  cardsContainer.appendChild(noneCard);
  cardsContainer.appendChild(createCard);
  wrap.appendChild(cardsContainer);

  const selectedId = getSelectedId();
  if (selectedId != null) {
    const preview = document.createElement('div');
    preview.className = 'vibe-card custom-prompt-card';
    preview.setAttribute('data-prompt-id', String(selectedId));
    const storedName = storage.get('selectedCustomPromptName');
    preview.innerHTML = `
      <div class="vibe-card-header">
        <div class="vibe-radio-indicator"></div>
        <span class="vibe-name"></span>
      </div>
    `;
    preview.querySelector('.vibe-name').textContent = typeof storedName === 'string' && storedName
      ? storedName
      : getCharacterModalText('custom_prompt', 'Custom prompt');
    preview.addEventListener('click', () => {
      if (cardsContainer.classList.contains('is-collapsed')) return;
      const match = savedPrompts.find((item) => Number(item.id) === selectedId);
      if (match) selectSaved(match);
    });
    cardsContainer.insertBefore(preview, createCard);
    markSelected(preview);
  } else {
    markSelected(noneCard);
  }

  return { wrap, selectNone };
};

/**
 * Creates the vibe selector for AI response style: a collapsible card picker plus
 * temperature and reasoning override sliders.
 * @returns {Promise<HTMLElement>} The vibe selector form group
 */
export async function createVibeSelector() {
  const formGroup = document.createElement('div');
  formGroup.className = 'form-group vibe-selector-group';

  const label = document.createElement('label');
  label.textContent = 'Response Style';
  label.setAttribute('data-translate', 'character_modal.response_style');
  formGroup.appendChild(label);

  const cardsContainer = document.createElement('div');
  cardsContainer.className = 'vibe-cards-container is-collapsed';

  const savedVibeId = await getUserVibe();
  const vibeOptions = await getVibeOptions();
  const selectedVibe = vibeOptions?.find(v => String(v.id) === String(savedVibeId)) ?? null;
  const { membershipTier } = await getUserData();

  // Override sliders (built before the cards so card selection can update reasoning availability).
  const overrideParams = document.createElement('div');
  overrideParams.className = 'vibe-override-params';

  const resetBtn = document.createElement('button');
  resetBtn.type = 'button';
  resetBtn.className = 'vibe-reset-btn';
  resetBtn.textContent = 'Reset';
  resetBtn.setAttribute('data-translate', 'character_modal.reset_params');
  resetBtn.setAttribute('aria-label', 'Restore model defaults');
  overrideParams.appendChild(resetBtn);

  const temperatureValue = await getUserTemperature();
  const temperatureSlider = createOverrideSlider({
    labelKey: 'character_modal.temperature',
    labelText: 'Temperature',
    infoKey: 'character_modal.temperature_info',
    infoText: 'Controls randomness. Lower stays focused and consistent; higher is more varied and creative.',
    min: 0,
    max: 1,
    step: 0.05,
    value: temperatureValue,
    minLabelKey: 'character_modal.temperature_min',
    minLabelText: 'Stable',
    maxLabelKey: 'character_modal.temperature_max',
    maxLabelText: 'Creative',
    formatValue: (v) => v.toFixed(2),
    onInput: (v) => {
      storage.set('selectedTemperature', v);
      schedulePersistResponseStyle();
    }
  });
  overrideParams.appendChild(temperatureSlider.row);

  const reasoningEffort = await getUserReasoningEffort();
  const reasoningIndex = Math.max(0, REASONING_EFFORTS.indexOf(reasoningEffort));
  const reasoningSlider = createOverrideSlider({
    labelKey: 'character_modal.reasoning',
    labelText: 'Reasoning',
    infoKey: 'character_modal.reasoning_info',
    infoText: 'How much the model thinks before replying. More reasoning is smarter but slower and costs more.',
    min: 0,
    max: REASONING_EFFORTS.length - 1,
    step: 1,
    value: reasoningIndex,
    minLabelKey: 'character_modal.reasoning_min',
    minLabelText: 'Fast / cheap',
    maxLabelKey: 'character_modal.reasoning_max',
    maxLabelText: 'Smarter / expensive',
    formatValue: (v) => reasoningEffortLabel(REASONING_EFFORTS[v]),
    onInput: (v) => {
      storage.set('selectedReasoningEffort', REASONING_EFFORTS[v]);
      schedulePersistResponseStyle();
    },
    isReasoning: true
  });
  overrideParams.appendChild(reasoningSlider.row);

  const maxTokensValue = await getUserMaxTokens();
  const maxTokensSlider = createOverrideSlider({
    labelKey: 'character_modal.max_tokens',
    labelText: 'Max tokens',
    infoKey: 'character_modal.max_tokens_info',
    infoText: 'Sets the maximum length of a reply.',
    min: 50,
    max: 400,
    step: 10,
    value: maxTokensValue,
    minLabelKey: 'character_modal.max_tokens_min',
    minLabelText: 'Short',
    maxLabelKey: 'character_modal.max_tokens_max',
    maxLabelText: 'Very long',
    formatValue: (v) => String(Math.round(v)),
    onInput: (v) => {
      storage.set('selectedMaxTokens', Math.round(v));
      schedulePersistResponseStyle();
    }
  });
  overrideParams.appendChild(maxTokensSlider.row);

  const contextTurnsValue = await getUserContextTurns();
  const contextTurnsSlider = createOverrideSlider({
    labelKey: 'character_modal.context_size',
    labelText: 'Context size',
    infoKey: 'character_modal.context_size_info',
    infoText: 'Controls how many messages the character can remember at once.',
    min: 1,
    max: 16,
    step: 1,
    value: contextTurnsValue,
    minLabelKey: 'character_modal.context_size_min',
    minLabelText: 'Short memory / cheap',
    maxLabelKey: 'character_modal.context_size_max',
    maxLabelText: 'Good memory / expensive',
    formatValue: (v) => String(Math.round(v)),
    onInput: (v) => {
      storage.set('selectedContextTurns', Math.round(v));
      schedulePersistResponseStyle();
    }
  });
  overrideParams.appendChild(contextTurnsSlider.row);

  const advanced = document.createElement('details');
  advanced.className = 'step2-advanced vibe-advanced';
  const advancedSummary = document.createElement('summary');
  advancedSummary.className = 'step2-advanced-toggle';
  advancedSummary.tabIndex = 0;
  advancedSummary.innerHTML = `
    <i class="fas fa-chevron-right step2-advanced-chevron" aria-hidden="true"></i>
    <span data-translate="character_modal.advanced">Advanced</span>
  `;
  const advancedContent = document.createElement('div');
  advancedContent.className = 'step2-advanced-content';

  const topPValue = await getUserTopP();
  const topPSlider = createOverrideSlider({
    labelKey: 'character_modal.top_p',
    labelText: 'Top P',
    infoKey: 'character_modal.top_p_info',
    infoText: 'Limits how wide the next-word choices can be. 0 uses the model default.',
    min: 0,
    max: 1,
    step: 0.05,
    value: topPValue ?? 0,
    minLabelKey: 'character_modal.top_p_min',
    minLabelText: 'Focused',
    maxLabelKey: 'character_modal.top_p_max',
    maxLabelText: 'Diverse',
    formatValue: (v) => (v <= 0 ? 'Default' : v.toFixed(2)),
    onInput: (v) => {
      storage.set('selectedTopP', v <= 0 ? 0 : v);
      schedulePersistResponseStyle();
    }
  });
  advancedContent.appendChild(topPSlider.row);

  const frequencyPenaltyValue = await getUserFrequencyPenalty();
  const frequencyPenaltySlider = createOverrideSlider({
    labelKey: 'character_modal.frequency_penalty',
    labelText: 'Frequency penalty',
    infoKey: 'character_modal.frequency_penalty_info',
    infoText: 'Reduces repeating the same words. 0 uses the model default.',
    min: 0,
    max: 2,
    step: 0.1,
    value: frequencyPenaltyValue ?? 0,
    minLabelKey: 'character_modal.frequency_penalty_min',
    minLabelText: 'Repetitive ok',
    maxLabelKey: 'character_modal.frequency_penalty_max',
    maxLabelText: 'Less repetition',
    formatValue: (v) => (v <= 0 ? 'Default' : v.toFixed(1)),
    onInput: (v) => {
      storage.set('selectedFrequencyPenalty', v <= 0 ? 0 : v);
      schedulePersistResponseStyle();
    }
  });
  advancedContent.appendChild(frequencyPenaltySlider.row);

  const presencePenaltyValue = await getUserPresencePenalty();
  const presencePenaltySlider = createOverrideSlider({
    labelKey: 'character_modal.presence_penalty',
    labelText: 'Presence penalty',
    infoKey: 'character_modal.presence_penalty_info',
    infoText: 'Encourages bringing up new topics. 0 uses the model default.',
    min: 0,
    max: 2,
    step: 0.1,
    value: presencePenaltyValue ?? 0,
    minLabelKey: 'character_modal.presence_penalty_min',
    minLabelText: 'Stay on topic',
    maxLabelKey: 'character_modal.presence_penalty_max',
    maxLabelText: 'New topics',
    formatValue: (v) => (v <= 0 ? 'Default' : v.toFixed(1)),
    onInput: (v) => {
      storage.set('selectedPresencePenalty', v <= 0 ? 0 : v);
      schedulePersistResponseStyle();
    }
  });
  advancedContent.appendChild(presencePenaltySlider.row);

  const nearlyEqual = (left, right) => Math.abs(Number(left) - Number(right)) < 1e-6;

  const syncResetVisibility = () => {
    const temperature = parseFloat(temperatureSlider.input.value);
    const reasoning = REASONING_EFFORTS[Number.parseInt(reasoningSlider.input.value, 10)];
    const maxTokens = Math.round(parseFloat(maxTokensSlider.input.value));
    const contextTurns = Math.round(parseFloat(contextTurnsSlider.input.value));
    const topP = parseFloat(topPSlider.input.value);
    const frequencyPenalty = parseFloat(frequencyPenaltySlider.input.value);
    const presencePenalty = parseFloat(presencePenaltySlider.input.value);
    const customPrompt = storage.get('selectedCustomPrompt');
    const hasCustomPrompt = typeof customPrompt === 'string' && customPrompt.trim().length > 0;
    const atDefault = nearlyEqual(temperature, DEFAULT_TEMPERATURE)
      && reasoning === DEFAULT_REASONING_EFFORT
      && maxTokens === DEFAULT_MAX_TOKENS
      && contextTurns === DEFAULT_CONTEXT_TURNS
      && !(topP > 0)
      && !(frequencyPenalty > 0)
      && !(presencePenalty > 0)
      && !hasCustomPrompt;
    resetBtn.hidden = atDefault;
    resetBtn.setAttribute('aria-hidden', atDefault ? 'true' : 'false');
  };

  const customPromptPicker = createCustomPromptPicker({ onSelectionChange: syncResetVisibility });
  advancedContent.appendChild(customPromptPicker.wrap);
  advanced.appendChild(advancedSummary);
  advanced.appendChild(advancedContent);
  overrideParams.appendChild(advanced);

  resetBtn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    temperatureSlider.setValue(DEFAULT_TEMPERATURE);
    reasoningSlider.setValue(REASONING_EFFORTS.indexOf(DEFAULT_REASONING_EFFORT));
    maxTokensSlider.setValue(DEFAULT_MAX_TOKENS);
    contextTurnsSlider.setValue(DEFAULT_CONTEXT_TURNS);
    topPSlider.setValue(0);
    frequencyPenaltySlider.setValue(0);
    presencePenaltySlider.setValue(0);
    customPromptPicker.selectNone();
    schedulePersistResponseStyle();
    syncResetVisibility();
  });

  overrideParams.addEventListener('input', () => {
    syncResetVisibility();
  });
  syncResetVisibility();

  // Close any open info tooltip when clicking outside an info button.
  overrideParams.addEventListener('click', (e) => {
    if (e.target instanceof Element && e.target.closest('.vibe-info-btn')) return;
    overrideParams.querySelectorAll('.vibe-override-row.info-visible').forEach(r => r.classList.remove('info-visible'));
  });

  const updateReasoningAvailability = (vibe) => {
    const capable = Boolean(vibe?.reasoning_capable);
    reasoningSlider.row.classList.toggle('is-disabled', !capable);
    if (!capable) reasoningSlider.row.classList.remove('tooltip-visible');
    reasoningSlider.input.disabled = !capable;
    reasoningSlider.row.setAttribute('aria-disabled', String(!capable));
  };

  // Expand/collapse arrow, kept inside whichever card is currently selected.
  const expandBtn = document.createElement('button');
  expandBtn.type = 'button';
  expandBtn.className = 'vibe-expand-btn';
  expandBtn.setAttribute('aria-label', 'Show all response styles');
  expandBtn.setAttribute('aria-expanded', 'false');
  expandBtn.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" d="M9 6l6 6-6 6"/></svg>';

  const setExpanded = (expanded) => {
    cardsContainer.classList.toggle('is-collapsed', !expanded);
    expandBtn.setAttribute('aria-expanded', String(expanded));
  };

  expandBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    setExpanded(cardsContainer.classList.contains('is-collapsed'));
  });

  const savedOverride = getUserModelOverride();
  const priceAnchor = await getResponseStylePriceAnchor();

  const customCard = document.createElement('div');
  customCard.className = 'vibe-card custom-model-card';
  customCard.setAttribute('aria-label', getCharacterModalText('custom_model', 'Custom model'));

  const customHeader = document.createElement('div');
  customHeader.className = 'vibe-card-header';
  const customRadio = document.createElement('div');
  customRadio.className = 'vibe-radio-indicator';
  const customName = document.createElement('span');
  customName.className = 'vibe-name';
  customName.textContent = getCharacterModalText('custom_model', 'Custom model');
  customName.setAttribute('data-translate', 'character_modal.custom_model');
  const customTags = document.createElement('div');
  customTags.className = 'vibe-tags-row';
  customTags.hidden = true;
  customHeader.append(customRadio, customName, customTags);

  const setCustomPriceBadge = (multiplier) => {
    customTags.replaceChildren();
    if (!Number.isFinite(multiplier)) {
      customTags.hidden = true;
      return;
    }
    const badge = createCreditMultiplierBadge(multiplier, { hideWhenOne: false, variant: 'chat' });
    if (!badge) {
      customTags.hidden = true;
      return;
    }
    customTags.hidden = false;
    customTags.appendChild(badge);
  };

  const customDescription = document.createElement('div');
  customDescription.className = 'vibe-description';
  customDescription.textContent = getCharacterModalText('custom_model_description', 'Any OpenRouter text model. Use provider/model.');
  customDescription.setAttribute('data-translate', 'character_modal.custom_model_description');

  const modelInput = document.createElement('input');
  modelInput.type = 'text';
  modelInput.className = 'custom-model-input';
  modelInput.autocomplete = 'off';
  modelInput.spellcheck = false;
  modelInput.placeholder = getCharacterModalText('custom_model_placeholder', 'provider/model');
  modelInput.setAttribute('data-translate-placeholder', 'character_modal.custom_model_placeholder');
  modelInput.setAttribute('aria-label', getCharacterModalText('custom_model', 'Custom model'));

  const modelStatus = document.createElement('p');
  modelStatus.className = 'custom-model-status';
  modelStatus.setAttribute('role', 'status');

  const modelError = document.createElement('p');
  modelError.className = 'custom-model-error';
  modelError.hidden = true;
  modelError.setAttribute('role', 'alert');

  customCard.append(customHeader, customDescription, modelInput, modelStatus, modelError);

  const formatPerMillion = (tokenPrice) => {
    const amount = Number(tokenPrice);
    if (!Number.isFinite(amount)) return null;
    const perMillion = amount * 1000000;
    if (perMillion === 0) return '$0';
    if (perMillion < 0.01) return `$${perMillion.toFixed(4)}`;
    if (perMillion < 1) return `$${perMillion.toFixed(3)}`;
    return `$${perMillion.toFixed(2)}`;
  };

  const markCustomSelected = (reasoningCapable) => {
    cardsContainer.querySelectorAll('.vibe-card').forEach(c => c.classList.remove('selected'));
    customCard.classList.add('selected');
    customCard.appendChild(expandBtn);
    updateReasoningAvailability({ reasoning_capable: reasoningCapable });
  };

  const clearModelFeedback = () => {
    modelStatus.textContent = '';
    modelError.hidden = true;
    modelInput.setAttribute('aria-invalid', 'false');
    setCustomPriceBadge(null);
  };

  let lookupTimer = null;
  let lookupSeq = 0;

  const showModelError = (reason) => {
    const messages = {
      format: ['custom_model_invalid', 'Use provider/model, with one slash.'],
      missing: ['custom_model_missing', 'That model was not found.'],
      not_text: ['custom_model_not_text', 'That model does not output text.'],
      unreachable: ['custom_model_unreachable', 'Could not reach OpenRouter to check this model.'],
      plus_locked: ['custom_model_plus_locked', 'This model class unlocks on Starter and above.'],
      max_locked: ['custom_model_max_locked', 'This model class unlocks on Premium and above.']
    };
    const [key, fallback] = messages[reason] || messages.unreachable;
    modelError.textContent = getCharacterModalText(key, fallback);
    modelError.setAttribute('data-translate', `character_modal.${key}`);
    modelError.hidden = false;
    modelStatus.textContent = '';
    modelInput.setAttribute('aria-invalid', 'true');
    setCustomPriceBadge(null);
    setUserModelOverride('');
    schedulePersistResponseStyle();
    markCustomSelected(false);
  };

  const confirmModelSlug = async (raw) => {
    const seq = ++lookupSeq;
    const trimmed = String(raw || '').trim();
    if (!trimmed) {
      clearModelFeedback();
      const vibeId = storage.get('selectedVibeId');
      const vibe = vibeOptions?.find((entry) => String(entry.id) === String(vibeId));
      const card = cardsContainer.querySelector(`[data-vibe-id="${vibeId}"]`);
      if (vibe && card) {
        selectCard(vibe, card);
        return;
      }
      setUserModelOverride('');
      schedulePersistResponseStyle();
      return;
    }
    modelError.hidden = true;
    modelInput.setAttribute('aria-invalid', 'false');
    modelStatus.textContent = getCharacterModalText('custom_model_checking', 'Checking model…');
    modelStatus.setAttribute('data-translate', 'character_modal.custom_model_checking');
    const result = await lookupOpenRouterTextModel(trimmed);
    if (seq !== lookupSeq) return;
    if (!result.ok) {
      showModelError(result.reason);
      return;
    }
    modelInput.value = result.id;
    clearModelFeedback();
    const inputPrice = formatPerMillion(result.pricing?.prompt);
    const outputPrice = formatPerMillion(result.pricing?.completion);
    modelStatus.textContent = inputPrice && outputPrice
      ? getCharacterModalText('custom_model_price', 'In {input} / Out {output} per 1M tokens')
        .replace('{input}', inputPrice)
        .replace('{output}', outputPrice)
      : result.name;
    modelStatus.removeAttribute('data-translate');
    const perMillion = openRouterPricingPerMillion(result.pricing);
    const multiplier = creditMultiplierFromPricing(perMillion, priceAnchor);
    if (!modelLaneSkipped() && !isMultiplierAllowedForMembership(multiplier, membershipTier)) {
      const lane = accessLaneFromMultiplier(multiplier) === 'max' ? 'max' : 'plus';
      showModelError(lane === 'max' ? 'max_locked' : 'plus_locked');
      showModelAccessRequiredModal({ lane });
      return;
    }
    setCustomPriceBadge(multiplier);
    setUserModelOverride(result.id);
    schedulePersistResponseStyle();
    markCustomSelected(result.reasoningCapable);
  };

  const selectCard = (vibe, card) => {
    if (lookupTimer) clearTimeout(lookupTimer);
    lookupSeq += 1;
    modelInput.value = '';
    clearModelFeedback();
    setUserModelOverride('');
    storage.set('selectedVibeId', vibe.id);
    schedulePersistResponseStyle();
    cardsContainer.querySelectorAll('.vibe-card').forEach(c => c.classList.remove('selected'));
    card.classList.add('selected');
    card.appendChild(expandBtn);
    updateReasoningAvailability(vibe);

    import('../../../analytics/hangout-obs-tracker.js').then(({ getAnalyticsTracker }) => {
      getAnalyticsTracker().trackFeature('response_style_selected');
    }).catch(() => { /* never block UI */ });
  };

  vibeOptions?.forEach(vibe => {
    const isSelected = !savedOverride && String(vibe.id) === String(savedVibeId);
    const laneSkipped = modelLaneSkipped();
    const accessLane = laneSkipped ? 'included' : getStyleAccessLane(vibe);
    const locked = !laneSkipped && !isStyleAllowedForMembership(vibe, membershipTier);
    const card = buildVibeCard(vibe, isSelected, { locked, accessLane });

    const handleActivate = () => {
      if (cardsContainer.classList.contains('is-collapsed')) return;
      if (locked) {
        showModelAccessRequiredModal({ lane: accessLane === 'max' ? 'max' : 'plus' });
        return;
      }
      selectCard(vibe, card);
      setExpanded(false);
    };

    card.addEventListener('click', () => {
      handleActivate();
    });
    card.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      handleActivate();
    });

    if (isSelected) card.appendChild(expandBtn);
    cardsContainer.appendChild(card);
  });

  const handleCustomActivate = () => {
    if (cardsContainer.classList.contains('is-collapsed') && !customCard.classList.contains('selected')) return;
    if (!customCard.classList.contains('selected')) markCustomSelected(false);
    modelInput.focus();
  };

  customCard.addEventListener('click', (event) => {
    if (event.target === modelInput) return;
    handleCustomActivate();
  });
  modelInput.addEventListener('click', (event) => {
    event.stopPropagation();
    if (!customCard.classList.contains('selected')) markCustomSelected(false);
  });
  modelInput.addEventListener('keydown', (event) => {
    event.stopPropagation();
  });
  modelInput.addEventListener('input', () => {
    if (!customCard.classList.contains('selected')) markCustomSelected(false);
    setUserModelOverride('');
    setCustomPriceBadge(null);
    schedulePersistResponseStyle();
    if (lookupTimer) clearTimeout(lookupTimer);
    lookupTimer = setTimeout(() => {
      lookupTimer = null;
      confirmModelSlug(modelInput.value);
    }, 400);
  });

  cardsContainer.appendChild(customCard);
  if (savedOverride) {
    modelInput.value = savedOverride;
    markCustomSelected(false);
    confirmModelSlug(savedOverride);
  }

  formGroup.appendChild(cardsContainer);
  formGroup.appendChild(overrideParams);

  if (!savedOverride) updateReasoningAvailability(selectedVibe);
  return formGroup;
}

async function fetchAdditionalCharacterData(characterId, isPrivate = false) {
  try {
    if (isPrivate) {
      console.log(`Fetching private character data for ${characterId} from worker`);

      const character = await withNetworkRetry(
        () => fetchPrivateCharacterById(characterId),
        `Fetching private character data for ${characterId}`
      );
      if (!character) {
        console.warn(`Private character ${characterId} not found in user's private characters`);
        return null;
      }

      return character;
    } else {
      const resolvedLanguage = getCurrentLanguage?.() || 'en';
      const normalizedLanguage = (typeof resolvedLanguage === 'string' && /^[A-Za-z0-9-]+$/.test(resolvedLanguage.trim()))
        ? resolvedLanguage.trim()
        : 'en';
      const candidateUrls = [
        `${CDN_URL}/data/characters/hangout/${normalizedLanguage}/${characterId}.json`,
        `${CDN_URL}/data/characters/hangout/en/${characterId}.json`,
        `${CDN_URL}/data/characters/hangout/${characterId}.json`
      ];
      const response = await withNetworkRetry(
        async () => {
          for (const url of [...new Set(candidateUrls)]) {
            const candidate = await fetch(url);
            if (candidate.ok) return candidate;
          }
          throw new Error('HTTP 404');
        },
        `Fetching character data for ${characterId}`
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    }
  } catch (err) {
    console.warn('Failed to fetch additional character data:', characterId, err);
    return null;
  }
}

function createFormGroup(labelText, value, type, width = '100%') {
  const formGroup = document.createElement('div');
  formGroup.className = 'form-group';
  formGroup.setAttribute('aria-labelledby', `label-${labelText.toLowerCase().replace(/\s/g, '-')}`);

  const label = document.createElement('label');
  label.id = `label-${labelText.toLowerCase().replace(/\s/g, '-')}`;

  const labelTranslationMap = {
    'Age': 'character_modal.age',
    'Height': 'character_modal.height',
    'Voice Type': 'character_modal.voice_type',
    'Ethnicity': 'character_modal.ethnicity',
    'Occupation': 'character_modal.occupation',
    'Birthdate': 'character_modal.birthdate',
    'Character Description': 'character_modal.character_description',
    'Example Dialogue': 'character_modal.example_dialogue'
  };

  if (labelTranslationMap[labelText]) {
    label.setAttribute('data-translate', labelTranslationMap[labelText]);
  }

  label.textContent = labelText;
  formGroup.appendChild(label);

  let displayValue;
  const displayText = value || 'N/A';
  const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);

  if (type === 'textarea') {
    displayValue = document.createElement('textarea');
    displayValue.className = 'value';
    displayValue.textContent = displayText;
    displayValue.readOnly = true;
    displayValue.style.minHeight = '120px';
    displayValue.setAttribute('aria-readonly', 'true');
  } else if (isNumeric && ['Bust Size', 'Fitness Level'].includes(labelText)) {
    displayValue = document.createElement('div');
    displayValue.className = 'stat-bar-container';

    const progress = document.createElement('progress');
    progress.className = 'stat-bar';
    const numValue = parseFloat(value) || 0;
    progress.max = labelText === 'Bust Size' ? 5 : 10;
    progress.value = numValue;
    Object.assign(progress, {
      'aria-valuenow': numValue,
      'aria-valuemin': 0,
      'aria-valuemax': progress.max
    });
    displayValue.appendChild(/** @type {Node} */((/** @type {unknown} */ (progress))));

    const valueText = document.createElement('span');
    valueText.className = 'stat-value-text';
    valueText.textContent = displayText;
    displayValue.appendChild(/** @type {Node} */((/** @type {unknown} */ (valueText))));
  } else if (type === 'select') {
    displayValue = document.createElement('select');
    displayValue.className = 'value';
    const option = document.createElement('option');
    Object.assign(option, {
      value: value || '',
      textContent: displayText,
      selected: true
    });
    displayValue.appendChild(option);
  } else {
    displayValue = document.createElement('div');
    displayValue.className = 'value';
    displayValue.textContent = displayText;
  }

  displayValue.style.width = width;
  formGroup.appendChild(displayValue);
  return formGroup;
}

async function handleVoiceChange(selectedVoiceId, character) {
  if (!selectedVoiceId) return;

  try {
    const normalizedGender = normalizeGender(character);
    const voiceSubDir = normalizedGender === 'male' ? 'male_voices' : 'voices';
    const voiceData = await getVoiceById(selectedVoiceId, voiceSubDir)
      || await getVoiceById(selectedVoiceId, 'voices')
      || await getVoiceById(selectedVoiceId, 'male_voices');
    if (!voiceData) return;

    const voicePreferences = storage.get('characterVoiceLanguages') || {};
    const currentLanguage = voicePreferences[character.id];
    const supportedLanguageCodes = getVoiceLanguageCodesForProvider(voiceData.provider);

    const newLanguage =
      (currentLanguage && supportedLanguageCodes.includes(currentLanguage) ? currentLanguage : null) ||
      (supportedLanguageCodes.includes('en') ? 'en' : null) ||
      supportedLanguageCodes[0] ||
      'en';

    // Default is English; keep it implicit (not stored).
    if (newLanguage === 'en') {
      delete voicePreferences[character.id];
    } else {
      voicePreferences[character.id] = newLanguage;
    }
    storage.set('characterVoiceLanguages', voicePreferences);

    const voiceIdPreferences = storage.get('characterVoiceIds') || {};
    voiceIdPreferences[character.id] = selectedVoiceId;
    storage.set('characterVoiceIds', voiceIdPreferences);

    if (voiceData?.provider) {
      const voiceProviderPreferences = storage.get('characterVoiceProviders') || {};
      voiceProviderPreferences[character.id] = voiceData.provider;
      storage.set('characterVoiceProviders', voiceProviderPreferences);
    }

    const voiceDropdown = document.querySelector(`.character-voice-dropbtn[data-character-id="${character.id}"]`);
    const flagImg = voiceDropdown?.querySelector('.voice-flag-icon');
    if (flagImg) {
      flagImg.remove();
      const newFlag = createFlagElement(LANGUAGE_TO_FLAG[newLanguage] || 'flag-gb', 'voice-flag-icon');
      newFlag.setAttribute('aria-label', `${LANGUAGE_TO_NAME[newLanguage] || 'English'} Voice`);
      voiceDropdown.insertBefore(newFlag, voiceDropdown.firstChild);
    }

    // Drop stale TTS clips for the previous voice so the next click regenerates.
    try {
      await invalidateCachedVoiceForCharacter(character.id, selectedVoiceId);
    } catch (invalidateError) {
      console.warn('Failed to invalidate cached voice audio after voice change:', invalidateError);
    }

    console.log(`🎵 Voice changed to ${voiceData.name} (${selectedVoiceId}) for character ${character.id}, language: ${newLanguage}`);
  } catch (error) {
    console.error('Error updating voice language preference:', error);
  }
}

async function persistCustomElevenLabsVoice(character, voiceId) {
  const voiceIdPreferences = storage.get('characterVoiceIds') || {};
  voiceIdPreferences[character.id] = voiceId;
  storage.set('characterVoiceIds', voiceIdPreferences);

  const voiceProviderPreferences = storage.get('characterVoiceProviders') || {};
  voiceProviderPreferences[character.id] = 'elevenlabs';
  storage.set('characterVoiceProviders', voiceProviderPreferences);

  try {
    await invalidateCachedVoiceForCharacter(character.id, voiceId);
  } catch (invalidateError) {
    console.warn('Failed to invalidate cached voice audio after custom voice change:', invalidateError);
  }
}

async function createPortraitSection(character, charPosIndex) {
  const portraitContainer = document.createElement('div');
  portraitContainer.className = 'character-portrait';

  let imageUrl = character.image_url;

  if (!imageUrl) {
    try {
      const normalizedGender = normalizeGender(character);
      const actionId = pickStandingActionId(normalizedGender);
      const outfitId = await getCurrentOutfitIdForCharacter(character.id);

      imageUrl = await fetchCharacterSpriteUrl(
        character.id,
        actionId,
        outfitId
      );
    } catch (error) {
      console.warn('Unable to fetch sprite for portrait:', error);
    }
  }

  if (imageUrl) {
    const avatarFrame = document.createElement('div');
    avatarFrame.className = 'persona-avatar-frame';
    avatarFrame.setAttribute('data-char-index', charPosIndex);

    const img = document.createElement('img');
    Object.assign(img, {
      src: imageUrl,
      alt: `${character.name} portrait`
    });

    avatarFrame.appendChild(img);
    portraitContainer.appendChild(avatarFrame);
  } else {
    const placeholder = document.createElement('div');
    placeholder.className = 'placeholder';
    placeholder.setAttribute('data-translate', 'character_modal.no_image');
    placeholder.textContent = 'No Image';
    portraitContainer.appendChild(placeholder);
  }

  return portraitContainer;
}

async function fetchCharacterSpritesInline(characterId, outfitId) {
  const resolvedCharId = characterId || '';
  const resolvedOutfitId = outfitId || 0;
  const searchParams = new URLSearchParams({
    character_id: String(resolvedCharId),
    outfit_id: String(resolvedOutfitId)
  });
  applySfwQueryParam(searchParams);

  const requestUrl = `${WORKER_URL}/get_character_sprites?${searchParams.toString()}`;

  const response = await withNetworkRetry(() => fetch(requestUrl, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' }
  }), 'Fetch character sprites');

  if (!response.ok) throw new Error(`Failed to fetch sprites: ${response.status}`);

  const payload = await response.json();
  const sprites = payload?.sprites || [];

  return sprites.map((sprite) => {
    const char_id = sprite.char_id ?? characterId;
    const outfit_id = sprite.outfit_id ?? outfitId;
    const mapped = {
      char_id,
      outfit_id,
      action_id: sprite.action_id,
      actionName: sprite.actionName || `action_${sprite.action_id}`
    };
    return {
      ...mapped,
      imageUrl: getSpriteSmallImagePath(mapped)
    };
  });
}

const spriteSelectionKey = (charId, outfitId, actionId) =>
  `${charId}_${outfitId}_${actionId}`;

const getPersistedSpriteSelections = (characterId, outfitId) => {
  const stored = storage.get('spriteGallerySelections') || {};
  const charSelections = stored[characterId]?.[outfitId];
  return charSelections ? new Set(charSelections) : null;
};

const parseActionIdsFromSelectionKeys = (selectedKeys) => {
  const actionIds = [];
  for (const key of selectedKeys) {
    const parts = String(key).split('_');
    if (parts.length >= 3) {
      const id = parseInt(parts[2], 10);
      if (!Number.isNaN(id)) actionIds.push(id);
    }
  }
  return actionIds;
};

const writeLocalSpriteSelections = (characterId, outfitId, selectedKeys) => {
  const stored = storage.get('spriteGallerySelections') || {};
  if (!stored[characterId]) stored[characterId] = {};
  stored[characterId][outfitId] = Array.from(selectedKeys);
  storage.set('spriteGallerySelections', stored);
};

const keysFromActionIds = (characterId, outfitId, actionIds) =>
  actionIds.map((actionId) => spriteSelectionKey(characterId, outfitId, actionId));

const fetchSpriteSelectionsFromBackend = async (characterId, outfitId) => {
  try {
    const session = await getSession();
    if (!session?.access_token) return null;

    const searchParams = new URLSearchParams({
      character_id: String(Number(characterId)),
      outfit_id: String(Number(outfitId))
    });
    const data = await fetchCache.fetchJSON(
      `${USER_WORKER_URL}/get-sprite-selections?${searchParams.toString()}`,
      {
        method: 'GET',
        skipCache: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        }
      }
    );

    if (!data?.success || !Array.isArray(data.action_ids) || data.action_ids.length === 0) {
      return null;
    }

    return data.action_ids
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id));
  } catch (err) {
    console.warn('Error fetching sprite selections from backend:', err);
    return null;
  }
};

const persistSpriteSelectionsToBackend = async (characterId, outfitId, selectedKeys) => {
  try {
    const session = await getSession();
    if (!session?.access_token) return false;

    const action_ids = parseActionIdsFromSelectionKeys(selectedKeys);
    const data = await fetchCache.fetchJSON(`${USER_WORKER_URL}/save-sprite-selections`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        character_id: Number(characterId),
        outfit_id: Number(outfitId),
        action_ids
      })
    });
    return data?.success !== false;
  } catch (err) {
    console.warn('Error persisting sprite selections to backend:', err);
    return false;
  }
};

const persistSpriteSelections = (characterId, outfitId, selectedKeys) => {
  writeLocalSpriteSelections(characterId, outfitId, selectedKeys);
  return persistSpriteSelectionsToBackend(characterId, outfitId, selectedKeys);
};

/**
 * Resolve latest assistant message for a character.
 * @param {Array<any>} messages
 * @param {string|number} characterId
 * @returns {any|null}
 */
function getLatestAssistantMessageForCharacter(messages, characterId) {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message?.role === 'assistant' && String(message.characterId) === String(characterId)) {
      return message;
    }
  }
  return null;
}

/**
 * Resolve the character's current action from the visible conversation, then live sprite state.
 * @param {string|number} characterId
 * @returns {number|null}
 */
const getCurrentActionIdForCharacter = (characterId) => {
  const hangoutState = HangoutState.getInstance();
  const visibleMessages = hangoutState.getVisibleMessages?.() || hangoutState.getMessages?.() || [];
  const latestAssistantMessage = getLatestAssistantMessageForCharacter(visibleMessages, characterId);
  const fromMsg = Number.parseInt(String(
    latestAssistantMessage?.actionId ?? latestAssistantMessage?.action_id ?? ''
  ), 10);
  if (Number.isFinite(fromMsg)) return fromMsg;

  const characters = hangoutState.getCharacters?.() || [];
  const charData = characters.find((c) => String(c.id) === String(characterId));
  const fromChar = Number.parseInt(String(
    charData?.spriteState?.action
    ?? charData?.spriteState?.actionId
    ?? charData?.actionId
    ?? charData?.action_id
    ?? ''
  ), 10);
  return Number.isFinite(fromChar) ? fromChar : null;
};

const smartActionIdAttr = (characterId) => {
  const actionId = getCurrentActionIdForCharacter(characterId);
  return Number.isFinite(actionId) ? String(actionId) : '';
};

const refreshOpenSpriteUnavailableUi = () => {
  document.querySelectorAll('.form-group[data-character-id]').forEach((section) => {
    if (typeof section.refreshSmartUnavailable === 'function') {
      section.refreshSmartUnavailable();
    }
  });
};

const applyHideUnavailableButtonCopy = (button, isHiding) => {
  if (!button) return;
  const hideLabel = getCharacterModalText('hide_unavailable_next_round', 'Hide unavailable (next round)');
  const showLabel = getCharacterModalText('show_unavailable_sprites', 'Show unavailable sprites');
  const label = isHiding ? showLabel : hideLabel;
  const translateKey = isHiding
    ? 'character_modal.show_unavailable_sprites'
    : 'character_modal.hide_unavailable_next_round';
  button.textContent = label;
  button.setAttribute('aria-label', label);
  button.setAttribute('data-translate', translateKey);
};

/**
 * Resolve current outfit ID for a character from config/state/message fallback chain.
 * @param {string|number} characterId
 * @returns {Promise<string|number>}
 */
async function getCurrentOutfitIdForCharacter(characterId) {
  const hasOutfitValue = (value) =>
    value !== undefined && value !== null && String(value).trim() !== '';

  const currentConfig = await storageManager.getItem('currentHangoutConfig');
  const configCharacters = currentConfig?.allCharacters || [];
  const configCharacter = configCharacters.find(c => String(c.id) === String(characterId));
  if (hasOutfitValue(configCharacter?.outfitId)) return configCharacter.outfitId;

  const hangoutState = HangoutState.getInstance();
  const characters = hangoutState.getCharacters?.() || [];
  const charData = characters.find(c => String(c.id) === String(characterId));
  if (hasOutfitValue(charData?.outfitId)) return charData.outfitId;

  const messages = hangoutState.getMessages?.() || [];
  const latestAssistantMessage = getLatestAssistantMessageForCharacter(messages, characterId);
  if (hasOutfitValue(latestAssistantMessage?.outfitId)) return latestAssistantMessage.outfitId;

  return 1;
}

const createSpriteSelectionOptionsSection = () => {
  const options = getSpriteSelectionOptions();

  const createOption = ({ storageKey, checked, translateKey, labelText }) => {
    const row = document.createElement('label');
    row.className = 'sprite-selection-option';

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.checked = checked;
    input.tabIndex = 0;
    input.dataset.spriteOption = storageKey;
    input.setAttribute('aria-label', labelText);
    input.addEventListener('change', () => {
      setSpriteSelectionOption(storageKey, input.checked);
      document.querySelectorAll(`input[data-sprite-option="${storageKey}"]`).forEach((other) => {
        if (other !== input) other.checked = input.checked;
      });
      refreshOpenSpriteUnavailableUi();
    });

    const text = document.createElement('span');
    text.setAttribute('data-translate', translateKey);
    text.textContent = labelText;

    row.appendChild(input);
    row.appendChild(text);
    return row;
  };

  const fragment = document.createDocumentFragment();
  const preventRepetition = createOption({
    storageKey: SPRITE_PREVENT_REPETITION_KEY,
    checked: options.preventRepetition,
    translateKey: 'character_modal.prevent_repetition',
    labelText: 'Prevent repetition'
  });
  const smartSelection = createOption({
    storageKey: SPRITE_USE_SMART_SELECTION_KEY,
    checked: options.useSmartSelection,
    translateKey: 'character_modal.use_smart_selection',
    labelText: 'Use smart selection'
  });
  const translationHost = document.createElement('div');
  translationHost.append(preventRepetition, smartSelection);
  applyTranslationsToContainer(translationHost, 'hangout', getCurrentLanguage());
  fragment.append(preventRepetition, smartSelection);
  return fragment;
};

async function ensureSpriteGalleryStylesheet() {
  const stylesheetHref = '/css/sprite-gallery.css';
  const existingLink = /** @type {HTMLLinkElement | null} */ (
    document.querySelector(`link[href="${stylesheetHref}"]`)
  );
  if (existingLink?.sheet) return;

  await new Promise((resolve) => {
    const complete = () => resolve();
    const waitTimeoutMs = 1500;

    if (existingLink) {
      existingLink.addEventListener('load', complete, { once: true });
      existingLink.addEventListener('error', complete, { once: true });
      setTimeout(complete, waitTimeoutMs);
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = stylesheetHref;
    link.addEventListener('load', complete, { once: true });
    link.addEventListener('error', complete, { once: true });
    document.head.appendChild(link);
    setTimeout(complete, waitTimeoutMs);
  });
}

async function createInlineSpriteGrid(character, outfitId) {
  await ensureSpriteGalleryStylesheet();

  const section = document.createElement('div');
  section.className = 'form-group';
  section.dataset.characterId = String(character.id);
  section.dataset.outfitId = String(outfitId);

  const label = document.createElement('label');
  label.setAttribute('data-translate', 'character_modal.sprites');
  label.textContent = 'Sprites';
  section.appendChild(label);
  section.appendChild(createSpriteSelectionOptionsSection());

  const statusEl = document.createElement('div');
  statusEl.className = 'sprite-gallery-status';
  section.appendChild(statusEl);

  const searchInput = document.createElement('input');
  searchInput.type = 'search';
  searchInput.className = 'sprite-gallery-search';
  searchInput.placeholder = 'Search by name';
  searchInput.setAttribute('aria-label', 'Filter sprites by name');
  searchInput.tabIndex = 0;
  section.appendChild(searchInput);

  const gridWrapper = document.createElement('div');
  gridWrapper.className = 'sprite-gallery-grid-wrap';

  const hideUnavailableBtn = document.createElement('button');
  hideUnavailableBtn.type = 'button';
  hideUnavailableBtn.className = 'sprite-gallery-btn sprite-gallery-btn-secondary sprite-gallery-hide-unavailable';
  hideUnavailableBtn.tabIndex = 0;
  hideUnavailableBtn.setAttribute('aria-pressed', 'false');
  applyHideUnavailableButtonCopy(hideUnavailableBtn, false);
  hideUnavailableBtn.hidden = true;
  section.appendChild(hideUnavailableBtn);

  let galleryHasSprites = false;
  const syncHideUnavailableChrome = () => {
    const { useSmartSelection } = getSpriteSelectionOptions();
    const showButton = galleryHasSprites && useSmartSelection;
    hideUnavailableBtn.hidden = !showButton;
    const isHiding = showButton && getHideSmartUnavailable();
    hideUnavailableBtn.setAttribute('aria-pressed', isHiding ? 'true' : 'false');
    applyHideUnavailableButtonCopy(hideUnavailableBtn, isHiding);
    gridWrapper.classList.toggle('is-hiding-unavailable', isHiding);
  };
  section.refreshSmartUnavailable = syncHideUnavailableChrome;

  const actionsRow = document.createElement('div');
  actionsRow.className = 'modal-header sprite-gallery-actions';

  const cancelBtn = document.createElement('button');
  cancelBtn.type = 'button';
  cancelBtn.className = 'sprite-gallery-btn sprite-gallery-btn-secondary';
  cancelBtn.setAttribute('aria-label', 'Cancel image changes');
  cancelBtn.textContent = 'Cancel Changes';

  const validateBtn = document.createElement('button');
  validateBtn.type = 'button';
  validateBtn.className = 'sprite-gallery-btn sprite-gallery-btn-primary';
  validateBtn.setAttribute('aria-label', 'Validate image selection');
  validateBtn.textContent = 'Validate';

  actionsRow.appendChild(cancelBtn);
  actionsRow.appendChild(validateBtn);

  const grid = document.createElement('div');
  grid.className = 'sprite-gallery-grid';

  const loadingEl = document.createElement('div');
  loadingEl.className = 'text-sm';
  loadingEl.style.cssText = 'text-align:center;padding:20px;color:rgba(255,255,255,0.6)';
  loadingEl.textContent = 'Loading sprites...';
  grid.appendChild(loadingEl);
  gridWrapper.appendChild(grid);
  section.appendChild(gridWrapper);
  section.appendChild(actionsRow);

  const applyNameFilter = bindSpriteGalleryNameFilter(searchInput, grid);

  // Fetch sprites async
  (async () => {
    try {
      const [{ membershipTier }, sprites, videos, backendActionIds] = await Promise.all([
        getUserData(),
        fetchCharacterSpritesInline(character.id, outfitId),
        fetchCharacterVideos(character.id, outfitId),
        fetchSpriteSelectionsFromBackend(character.id, outfitId),
      ]);
      const actionCap = getActionCap(hasPaidMembership(membershipTier));
      const videoByActionId = buildVideoByActionId(videos);
      grid.innerHTML = '';

      if (!sprites.length) {
        statusEl.textContent = '';
        statusEl.classList.remove('is-over-cap');
        searchInput.hidden = true;
        const empty = document.createElement('div');
        empty.className = 'text-sm';
        empty.style.cssText = 'text-align:center;padding:20px;color:rgba(255,255,255,0.6)';
        empty.textContent = 'No sprites available for this outfit.';
        grid.appendChild(empty);
        cancelBtn.disabled = true;
        validateBtn.disabled = true;
        galleryHasSprites = false;
        syncHideUnavailableChrome();
        return;
      }

      // Load DB prefs, then localStorage, then default to all selected
      const allKeys = sprites.map(s => spriteSelectionKey(s.char_id, s.outfit_id, s.action_id));
      const persisted = getPersistedSpriteSelections(character.id, outfitId);
      const backendKeys = Array.isArray(backendActionIds) && backendActionIds.length > 0
        ? new Set(keysFromActionIds(character.id, outfitId, backendActionIds))
        : null;
      const initialSelectedKeys = backendKeys || persisted || new Set(allKeys);
      const selectedKeys = new Set(initialSelectedKeys);

      if (backendKeys) {
        writeLocalSpriteSelections(character.id, outfitId, backendKeys);
      }

      /** @type {Map<string, { item: HTMLElement, img: HTMLImageElement, sprite: object }>} */
      const itemByKey = new Map();

      const updateStatusAndTruncation = () => {
        const { useSmartSelection } = getSpriteSelectionOptions();
        const hiding = useSmartSelection && getHideSmartUnavailable();
        let selectedCount = selectedKeys.size;
        let totalCount = sprites.length;
        if (hiding) {
          selectedCount = 0;
          totalCount = 0;
          itemByKey.forEach(({ item }, key) => {
            if (item.classList.contains('is-smart-unavailable')) return;
            totalCount += 1;
            if (selectedKeys.has(key)) selectedCount += 1;
          });
        }
        const isOverCap = isSpriteSelectionOverCap(selectedCount, actionCap);
        statusEl.textContent = formatSpriteGalleryStatus(selectedCount, totalCount, actionCap);
        statusEl.classList.toggle('is-over-cap', isOverCap);
        const truncatedKeys = getTruncatedSelectionKeys(sprites, selectedKeys, actionCap);
        itemByKey.forEach(({ item }, key) => {
          item.classList.toggle('is-truncated', truncatedKeys.has(key));
        });
      };

      const applySmartUnavailableMarks = () => {
        const options = getSpriteSelectionOptions();
        if (!options.useSmartSelection) {
          itemByKey.forEach(({ item }) => item.classList.remove('is-smart-unavailable'));
          section.dataset.smartActionId = smartActionIdAttr(character.id);
          syncHideUnavailableChrome();
          updateStatusAndTruncation();
          return;
        }

        const actionNames = sprites.map((sprite) => sprite.actionName);
        const familyByName = new Map();
        sprites.forEach((sprite) => {
          familyByName.set(sprite.actionName, classifyActionFamily({ name: sprite.actionName }));
        });
        const currentActionId = getCurrentActionIdForCharacter(character.id);
        const currentSprite = sprites.find((sprite) => Number(sprite.action_id) === Number(currentActionId));
        const currentActionName = currentSprite?.actionName || '';
        const currentFamily = currentActionName
          ? classifyActionFamily({ name: currentActionName })
          : ACTION_FAMILIES.BASE;
        const hangoutState = HangoutState.getInstance();
        const seed = [
          hangoutState.getConversationId?.() || '',
          character.id ?? '',
          currentActionName || ''
        ].join(':');
        const kept = new Set(applySmartFamilyFilter(actionNames, {
          currentFamily,
          getFamily: (name) => familyByName.get(name) || null,
          seed
        }));

        itemByKey.forEach(({ item, sprite }) => {
          const isCurrent = Number.isFinite(currentActionId)
            && Number(sprite.action_id) === currentActionId;
          const isUnavailable = !kept.has(sprite.actionName)
            || (options.preventRepetition && isCurrent);
          item.classList.toggle('is-smart-unavailable', isUnavailable);
        });
        section.dataset.smartActionId = Number.isFinite(currentActionId) ? String(currentActionId) : '';
        syncHideUnavailableChrome();
        updateStatusAndTruncation();
      };

      const applySelectionToDom = () => {
        itemByKey.forEach(({ item, img }, key) => {
          const isSelected = selectedKeys.has(key);
          if (isSelected) {
            item.classList.add('is-selected');
            item.setAttribute('aria-pressed', 'true');
            img.classList.remove('is-deselected');
          } else {
            item.classList.remove('is-selected');
            item.setAttribute('aria-pressed', 'false');
            img.classList.add('is-deselected');
          }
        });
        updateStatusAndTruncation();
      };

      sprites.forEach((sprite) => {
        const key = spriteSelectionKey(sprite.char_id, sprite.outfit_id, sprite.action_id);
        const isSelected = selectedKeys.has(key);
        const displayName = formatSpriteNameForDisplay(sprite.actionName);

        const video = videoByActionId.get(String(sprite.action_id));
        const badgeText = getVideoBadgeText(video?.status);

        const item = document.createElement('div');
        item.className = 'sprite-gallery-item';
        if (isSelected) item.classList.add('is-selected');
        if (hasExistingVideo(video)) item.classList.add('has-video');
        item.setAttribute('role', 'button');
        item.setAttribute('tabindex', '0');
        item.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        item.setAttribute('aria-label', displayName);
        item.dataset.spriteKey = key;
        item.dataset.actionName = displayName.toLowerCase();

        if (badgeText) {
          const badge = document.createElement('span');
          badge.className = 'sprite-has-video-badge';
          badge.textContent = badgeText;
          item.appendChild(badge);
        }

        const img = document.createElement('img');
        img.src = `${CDN_URL}${sprite.imageUrl}`;
        img.alt = displayName;
        img.loading = 'lazy';
        img.className = 'sprite-gallery-image';
        if (!isSelected) img.classList.add('is-deselected');
        img.onerror = () => { img.src = '/assets/images/placeholder.svg'; };

        attachSpriteContextMenuListeners(img, {
          getOverrideSpriteData: (mediaEl) => ({
            characterId: sprite.char_id,
            outfitId: sprite.outfit_id,
            actionId: sprite.action_id,
            actionName: sprite.actionName || '',
            spriteUrl: mediaEl.currentSrc || mediaEl.src || '',
            path: null,
            isVideo: false,
            source: 'sprite-gallery-image'
          })
        });

        const labelEl = document.createElement('div');
        labelEl.className = 'sprite-gallery-label';

        const actionLine = document.createElement('div');
        actionLine.className = 'sprite-gallery-label-line sprite-gallery-label-action';
        actionLine.textContent = displayName;

        labelEl.appendChild(actionLine);

        const handleToggle = () => {
          if (selectedKeys.has(key)) {
            selectedKeys.delete(key);
            item.classList.remove('is-selected');
            item.setAttribute('aria-pressed', 'false');
            img.classList.add('is-deselected');
          } else {
            selectedKeys.add(key);
            item.classList.add('is-selected');
            item.setAttribute('aria-pressed', 'true');
            img.classList.remove('is-deselected');
          }
          updateStatusAndTruncation();
        };

        item.addEventListener('click', handleToggle);
        item.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        });

        item.appendChild(img);
        item.appendChild(labelEl);
        grid.appendChild(item);

        itemByKey.set(key, { item, img, sprite });
      });

      updateStatusAndTruncation();
      galleryHasSprites = true;
      applySmartUnavailableMarks();
      section.refreshSmartUnavailable = applySmartUnavailableMarks;

      const handleHideUnavailableClick = () => {
        if (!getSpriteSelectionOptions().useSmartSelection) return;
        setHideSmartUnavailable(!getHideSmartUnavailable());
        applySmartUnavailableMarks();
      };
      hideUnavailableBtn.addEventListener('click', handleHideUnavailableClick);

      if (typeof applyNameFilter === 'function') {
        applyNameFilter();
      }

      cancelBtn.addEventListener('click', () => {
        selectedKeys.clear();
        initialSelectedKeys.forEach(k => selectedKeys.add(k));
        applySelectionToDom();
      });

      validateBtn.addEventListener('click', async () => {
        validateBtn.disabled = true;
        validateBtn.classList.add('sprite-gallery-btn-loading');
        validateBtn.textContent = 'Saving...';

        let ok = false;
        try {
          ok = await persistSpriteSelections(character.id, outfitId, selectedKeys);
        } finally {
          validateBtn.disabled = false;
          validateBtn.classList.remove('sprite-gallery-btn-loading');
          validateBtn.textContent = 'Validate';
        }

        import('../../../utils/UnifiedNotificationSystem.js').then(({ showSuccess, showWarning }) => {
          if (ok) showSuccess('Sprite preferences saved.');
          else showWarning('Could not save sprite preferences. Changes saved locally.');
        }).catch(() => { /* never block UI */ });

        // update "initial" baseline to current for future cancels
        initialSelectedKeys.clear();
        selectedKeys.forEach(k => initialSelectedKeys.add(k));

      });
    } catch (error) {
      console.warn('Failed to load inline sprites:', error);
      grid.innerHTML = '';
      statusEl.textContent = '';
      statusEl.classList.remove('is-over-cap');
      const errorEl = document.createElement('div');
      errorEl.className = 'text-sm';
      errorEl.style.cssText = 'text-align:center;padding:20px;color:rgba(255,255,255,0.6)';
      errorEl.textContent = 'Failed to load sprites.';
      grid.appendChild(errorEl);
      cancelBtn.disabled = true;
      validateBtn.disabled = true;
    }
  })();

  return section;
}

function mergeCharacterData(character, extraData) {
  if (!extraData) return character;

  if (!character.info && extraData.info) {
    character.info = extraData.info;
  }

  return character;
}

/**
 * Returns a copy of the character's `info` object for the dynamic field grid.
 * @param {{ info?: Record<string, unknown>|null }} character
 * @returns {Record<string, unknown>}
 */
function buildCharacterInfoForDisplay(character) {
  return character?.info && typeof character.info === 'object'
    ? { ...character.info }
    : {};
}

async function populateCharacterInfoPanel(panelContent, characterIndex, options = {}) {
  const {
    showTabs = true,
    showClose = true,
    onClose = closeCharacterInfoModal
  } = options;

  panelContent.innerHTML = '';

  const hangoutConfig = await storageManager.getItem('currentHangoutConfig');
  const selectedChars = hangoutConfig?.allCharacters || [];

  let character = selectedChars.length > 0
    ? selectedChars.find(c => String(c.id) === String(characterIndex)) || selectedChars[0]
    : HangoutState.getInstance().getSelectedCharacter();

  if (!character?.id || !character.info || character.voice_id === undefined) {
    try {
      character = await HangoutService.getInstance().getCharacterDetails(character?.id || character);
    } catch (error) {
      console.error('Failed to fetch character details:', error);
      character = {
        id: character?.id || 'unknown',
        name: character?.name || `Unknown Character ${selectedChars.length + 1}`,
        info: null,
        voice_type: 'Unknown'
      };
    }
  }

  if (!character.info && character.id) {
    try {
      const isPrivate = character.rank === 'Private';
      const extraData = await fetchAdditionalCharacterData(character.id, isPrivate);
      character = mergeCharacterData(character, extraData);
    } catch (err) {
      console.warn('Error merging additional character info:', err);
    }
  }

  // Header - uses CSS classes instead of inline styles
  const panelHeader = document.createElement('div');
  panelHeader.className = 'modal-header';

  if (showClose) {
    const closeButton = document.createElement('button');
    closeButton.className = 'close-modal';
    closeButton.textContent = '×';
    closeButton.addEventListener('click', () => {
      const isMobile = window.innerWidth <= 768;
      const handleClose = () => onClose();
      if (isMobile) {
        setTimeout(handleClose, 0);
      } else {
        handleClose();
      }
    });

    panelHeader.appendChild(closeButton);
  }
  panelContent.appendChild(panelHeader);

  // Character tabs
  if (showTabs) {
    const characterTabs = createCharacterTabs(selectedChars, character, {
      onSelect: switchToCharacter
    });
    if (characterTabs) {
      characterTabs.style.margin = '15px 0 10px';
      panelContent.appendChild(characterTabs);
    }
  }

  const title = document.createElement('h2');
  title.textContent = character.name;
  panelContent.appendChild(title);

  // Separator - uses CSS class instead of inline style
  const separator = document.createElement('div');
  separator.className = 'character-info-separator';
  panelContent.appendChild(separator);

  const charPosIndex = selectedChars.length > 0
    ? selectedChars.findIndex(c => String(c.id) === String(character.id))
    : 0;

  const portraitContainer = await createPortraitSection(character, charPosIndex);
  panelContent.appendChild(portraitContainer);

  const characterDetails = document.createElement('form');
  characterDetails.className = 'character-info-form creation-form';

  const outfitId = await getCurrentOutfitIdForCharacter(character.id);
  const spriteGridSection = await createInlineSpriteGrid(character, outfitId);
  characterDetails.appendChild(spriteGridSection);

  const voiceRow = document.createElement('div');
  voiceRow.className = 'form-row';

  const normalizedGender = normalizeGender(character);
  const voiceSubDir = normalizedGender === 'male' ? 'male_voices' : 'voices';
  console.log(`Character ${character.id} (${character.name}): gender=${character.gender}, normalized=${normalizedGender}, voiceSubDir=${voiceSubDir}`);

  const voiceControlsGroup = document.createElement('div');
  voiceControlsGroup.className = 'form-group voice-controls-group';
  voiceControlsGroup.setAttribute('aria-labelledby', 'label-voice-type');

  const voiceLabel = document.createElement('label');
  voiceLabel.id = 'label-voice-type';
  voiceLabel.setAttribute('data-translate', 'character_modal.voice_type');
  voiceLabel.textContent = 'Voice';
  voiceControlsGroup.appendChild(voiceLabel);

  const voiceSelectPlaceholder = document.createElement('select');
  voiceSelectPlaceholder.className = 'value';
  voiceControlsGroup.appendChild(voiceSelectPlaceholder);

  const customVoiceNote = document.createElement('p');
  customVoiceNote.className = 'elevenlabs-voice-id-note';
  const customVoiceNoteText = document.createElement('span');
  customVoiceNoteText.setAttribute('data-translate', 'character_modal.elevenlabs_voice_id_note');
  customVoiceNoteText.textContent = 'Private voices need an ElevenLabs key on your OpenRouter account.';
  const customVoiceNoteLink = document.createElement('a');
  customVoiceNoteLink.href = 'https://openrouter.ai/integrations';
  customVoiceNoteLink.target = '_blank';
  customVoiceNoteLink.rel = 'noopener noreferrer';
  customVoiceNoteLink.setAttribute('data-translate', 'character_modal.elevenlabs_voice_id_link');
  customVoiceNoteLink.textContent = 'OpenRouter integrations';
  const customVoiceGuideLink = document.createElement('a');
  customVoiceGuideLink.href = '/guide/byok#voices';
  customVoiceGuideLink.target = '_blank';
  customVoiceGuideLink.rel = 'noopener noreferrer';
  customVoiceGuideLink.setAttribute('data-translate', 'character_modal.elevenlabs_voice_id_guide');
  customVoiceGuideLink.textContent = 'How voices work';
  customVoiceNote.hidden = true;
  customVoiceNote.appendChild(customVoiceNoteText);
  customVoiceNote.appendChild(document.createTextNode(' '));
  customVoiceNote.appendChild(customVoiceNoteLink);
  customVoiceNote.appendChild(document.createTextNode(' '));
  customVoiceNote.appendChild(customVoiceGuideLink);
  voiceControlsGroup.appendChild(customVoiceNote);

  const voiceLanguageControl = await createVoiceLanguageControl(character);

  const previewBtn = document.createElement('button');
  previewBtn.type = 'button';
  previewBtn.className = 'voice-preview-btn';
  previewBtn.setAttribute('aria-label', 'Preview selected voice');
  previewBtn.innerHTML = '<i class="fas fa-play" aria-hidden="true"></i> Preview';
  let previewAudio = null;
  const previewIndexByVoiceId = Object.create(null);
  previewBtn.addEventListener('click', async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const voiceDropdown = voiceControlsGroup.querySelector('.character-voice-type-control');
    const voiceId = voiceDropdown?.getValue?.();
    if (!voiceId) {
      showToast('Select a voice first', 'exclamation-triangle');
      return;
    }

    if (!Object.hasOwn(previewIndexByVoiceId, voiceId)) {
      previewIndexByVoiceId[voiceId] = randomPreviewIndex();
    }
    const previewIndex = previewIndexByVoiceId[voiceId];
    previewBtn.disabled = true;
    try {
      const preview = await fetchVoicePreview({
        voiceId,
        previewIndex,
        language: voiceLanguageControl?.getValue?.() || 'en',
      });
      previewIndexByVoiceId[voiceId] = preview.nextIndex;
      if (previewAudio) {
        previewAudio.pause();
        previewAudio = null;
      }
      previewAudio = new Audio(preview.objectUrl);
      await previewAudio.play();
    } catch (error) {
      console.error('Voice preview failed:', error);
      if (error?.body?.code === 'unknown_voice') {
        const messageKey = error.body.byok ? 'unknown_voice_byok' : 'unknown_voice';
        const fallback = error.body.byok
          ? "We don't recognize this voice id. If you want to use a private voice, make sure to configure an ElevenLabs key on your OpenRouter account."
          : "We don't recognize this voice id. If you want to use a private voice, make sure to use your own OpenRouter API key + configure an ElevenLabs key on your OpenRouter account.";
        showToast(getCharacterModalText(messageKey, fallback), 'exclamation-triangle', {
          duration: 0,
          closable: true
        });
        return;
      }
      showToast(error.message || 'Voice preview failed', 'error');
    } finally {
      previewBtn.disabled = false;
    }
  });

  voiceControlsGroup.appendChild(voiceLanguageControl);
  voiceControlsGroup.appendChild(previewBtn);
  voiceRow.appendChild(voiceControlsGroup);
  characterDetails.appendChild(voiceRow);

  /** @type {HTMLElement|null} */
  const voiceLangControl = voiceLanguageControl;

  try {
    const defaultVoiceText = window.translations?.[getCurrentLanguage()]?.hangout?.select_voice ?? 'Select a Voice';
    populateVoiceSelect(voiceSelectPlaceholder, defaultVoiceText, null, voiceSubDir)
      .then(async (customDropdown) => {
        const voiceIdPreferences = storage.get('characterVoiceIds') || {};
        const storedVoiceId = voiceIdPreferences[character.id];
        const { getAllVoices, pickDefaultElevenLabsVoice } = await import('../../../services/voiceService.js');

        let voiceIdToSet = null;
        let availableVoices = null;
        let customVoiceActive = false;

        if (storedVoiceId && isElevenLabsProviderVoiceId(storedVoiceId)) {
          const catalogVoice = await getVoiceById(storedVoiceId, voiceSubDir)
            || await getVoiceById(storedVoiceId, 'voices')
            || await getVoiceById(storedVoiceId, 'male_voices');
          if (!catalogVoice) {
            customVoiceActive = true;
            customVoiceNote.hidden = false;
            customDropdown.selectUnlisted?.({
              value: storedVoiceId,
              label: storedVoiceId,
              dataset: {
                voiceName: storedVoiceId,
                voiceProvider: 'elevenlabs',
                customVoice: 'true',
              },
            }, { silent: true });
            syncFixedVoiceLanguageControl({
              character,
              controlEl: voiceLangControl,
              provider: 'elevenlabs',
            });
          }
        }

        // Prefer locally active TTS preference over CDN character.voice_id
        if (storedVoiceId && !customVoiceActive) {
          voiceIdToSet = storedVoiceId;
          console.log(`🎵 Using stored voice preference for character ${character.id}: ${storedVoiceId}`);
        }

        if (!voiceIdToSet && character.voice_id) {
          try {
            availableVoices = await getAllVoices(voiceSubDir);

            if (availableVoices && availableVoices.length > 0) {
              const jsonVid = character.voice_id;
              const jsonVidStr = String(jsonVid);
              const voiceToFind = availableVoices.find(v =>
                (/** @type {any} */ (v)).provider_voice_id === jsonVid ||
                String((/** @type {any} */ (v)).provider_voice_id) === jsonVidStr ||
                v.id === jsonVid ||
                String(v.id) === jsonVidStr
              );

              if (voiceToFind) {
                voiceIdToSet = voiceToFind.id;
                console.log(`🎵 Using character's JSON voice_id: ${voiceToFind.name} (${voiceIdToSet})`);
              } else {
                console.warn(`Character's JSON voice_id ${character.voice_id} not found in available voices, falling back to ElevenLabs default`);
              }
            }
          } catch (error) {
            console.warn('Error validating JSON voice_id:', error);
          }
        }

        if (!voiceIdToSet && !customVoiceActive) {
          try {
            if (!availableVoices) {
              availableVoices = await getAllVoices(voiceSubDir);
            }

            if (availableVoices && availableVoices.length > 0) {
              const defaultVoice = pickDefaultElevenLabsVoice(availableVoices);

              if (defaultVoice) {
                voiceIdToSet = defaultVoice.id;

                if (voiceIdToSet && !storedVoiceId) {
                  const nextPrefs = storage.get('characterVoiceIds') || {};
                  nextPrefs[character.id] = voiceIdToSet;
                  storage.set('characterVoiceIds', nextPrefs);

                  if (defaultVoice.provider) {
                    const providerPrefs = storage.get('characterVoiceProviders') || {};
                    providerPrefs[character.id] = defaultVoice.provider;
                    storage.set('characterVoiceProviders', providerPrefs);
                  }

                  console.log(`🎵 Auto-saved default ElevenLabs voice for character ${character.id}: ${defaultVoice.name} (${voiceIdToSet})`);
                }

                console.log(`🎵 No saved voice for character ${character.id}, using ElevenLabs default: ${defaultVoice.name} (${voiceIdToSet})`);
              }
            }
          } catch (error) {
            console.warn('Error determining default voice:', error);
          }
        }

        if (voiceIdToSet) {
          if (typeof customDropdown._setValue === 'function') {
            customDropdown._setValue(voiceIdToSet);
          } else {
            customDropdown.setValue(voiceIdToSet);
          }
          const providerPrefs = storage.get('characterVoiceProviders') || {};
          syncFixedVoiceLanguageControl({
            character,
            controlEl: voiceLangControl,
            provider: providerPrefs[character.id] || null,
          });
        }

        customDropdown.addEventListener('change', (event) => {
          const nextVoiceId = event?.detail?.value;
          const isCustom = event?.detail?.customVoice === true;
          customVoiceNote.hidden = !isCustom;
          if (isCustom && nextVoiceId) {
            persistCustomElevenLabsVoice(character, nextVoiceId);
            syncFixedVoiceLanguageControl({
              character,
              controlEl: voiceLangControl,
              provider: 'elevenlabs',
            });
            return;
          }
          handleVoiceChange(nextVoiceId, character);
          const providerPrefs = storage.get('characterVoiceProviders') || {};
          syncFixedVoiceLanguageControl({
            character,
            controlEl: voiceLangControl,
            provider: event?.detail?.voiceProvider || providerPrefs[character.id] || null,
          });
        });
      })
      .catch(error => {
        console.error('Error populating voice options:', error);
        voiceSelectPlaceholder.innerHTML = `
          <option value="">Select a Voice</option>
          <option value="charismatic" ${character.voice_type === 'charismatic' ? 'selected' : ''}>Charismatic</option>
          <option value="energetic" ${character.voice_type === 'energetic' ? 'selected' : ''}>Energetic</option>
          <option value="calm" ${character.voice_type === 'calm' ? 'selected' : ''}>Calm</option>
          <option value="neutral" ${character.voice_type === 'neutral' ? 'selected' : ''}>Neutral</option>
        `;
      });
  } catch (error) {
    console.error('populateVoiceSelect failed synchronously:', error);
  }

  appendCharacterInfoFields(buildCharacterInfoForDisplay(character), characterDetails, {
    characterId: character.id,
    characterName: character.name || 'Character'
  });

  panelContent.appendChild(characterDetails);
}

export async function createCharacterInfoModal(characterIndex = 0) {
  if (window._creatingCharacterModal) {
    console.log('Character modal already being created, skipping duplicate request');
    return document.getElementById('character-info-modal');
  }

  window._creatingCharacterModal = true;

  try {
    let sidePanel = document.getElementById('character-info-modal');
    let panelContent;

    if (sidePanel) {
      panelContent = sidePanel.querySelector('.character-info-panel');
      panelContent.innerHTML = '';
    } else {
      sidePanel = document.createElement('div');
      sidePanel.id = 'character-info-modal';
      sidePanel.className = 'character-info-container';
      Object.assign(sidePanel, {
        role: 'dialog',
        'aria-modal': 'true'
      });

      panelContent = document.createElement('div');
      panelContent.className = 'character-info-panel';
      sidePanel.appendChild(panelContent);
      document.body.appendChild(sidePanel);

      sidePanel.addEventListener('click', e => {
        if (!panelContent.contains(e.target)) {
          const isMobile = window.innerWidth <= 768;
          if (isMobile) {
            setTimeout(() => {
              closeCharacterInfoModal();
            }, 0);
          } else {
            closeCharacterInfoModal();
          }
        }
      });

      const handleKeydown = e => {
        if (e.key === 'Escape') closeCharacterInfoModal();
      };
      document.addEventListener('keydown', handleKeydown);
      sidePanel._keydownHandler = handleKeydown;
    }

    await populateCharacterInfoPanel(panelContent, characterIndex);

    const { applyTranslations, getCurrentLanguage } = await import('../../../translations.js');
    const currentLang = getCurrentLanguage();
    applyTranslations('hangout', currentLang);
    applyCharacterInfoDynamicTranslations(panelContent);

    setTimeout(() => panelContent.classList.add('show'), 50);

    return sidePanel;
  } finally {
    window._creatingCharacterModal = false;
  }
}

// Re-export createCharacterTabs for other modules that may need it
export { createCharacterTabs };

async function switchToCharacter(characterId) {
  try {
    const existingModal = document.getElementById('character-info-modal');
    const panelContent = existingModal?.querySelector('.character-info-panel');

    if (existingModal && panelContent) {
      const scrollTop = panelContent.scrollTop || 0;

      await populateCharacterInfoPanel(panelContent, characterId);

      const { applyTranslations, getCurrentLanguage } = await import('../../../translations.js');
      const currentLang = getCurrentLanguage();
      applyTranslations('hangout', currentLang);
      applyCharacterInfoDynamicTranslations(panelContent);

      panelContent.scrollTop = scrollTop;
    } else {
      await createCharacterInfoModal(characterId);
      openCharacterInfoModal();
    }
  } catch (error) {
    console.error('Error switching character:', error);
  }
}

export async function renderCharacterInfoContent(panelContent, characterId, options = {}) {
  await populateCharacterInfoPanel(panelContent, characterId, {
    showTabs: false,
    showClose: false,
    ...options
  });

  const { applyTranslations, getCurrentLanguage } = await import('../../../translations.js');
  const currentLang = getCurrentLanguage();
  applyTranslations('hangout', currentLang);
  applyCharacterInfoDynamicTranslations(panelContent);
}

/**
 * If the sprite grid in the panel is for a different outfit (e.g. character switched outfit
 * since the modal was last opened), replace it. If only the live action changed, recompute
 * smart-selection marks.
 * @param {HTMLElement} panelContent - .character-info-panel element
 */
export async function refreshSpriteGridForCurrentOutfit(panelContent) {
  const gridWrap = panelContent.querySelector('.sprite-gallery-grid-wrap');
  const gridSection = gridWrap?.closest('.form-group');
  if (!gridSection?.dataset.characterId) return;

  const currentCharacterId = panelContent.querySelector('.character-tab-button.active')?.getAttribute('data-character-id')
    || gridSection.dataset.characterId;
  const currentOutfitId = await getCurrentOutfitIdForCharacter(currentCharacterId);
  if (gridSection.dataset.outfitId === String(currentOutfitId) && gridSection.dataset.characterId === String(currentCharacterId)) {
    if (typeof gridSection.refreshSmartUnavailable === 'function') {
      gridSection.refreshSmartUnavailable();
    }
    return;
  }

  let character = null;
  try {
    const hangoutConfig = await storageManager.getItem('currentHangoutConfig');
    const selectedChars = hangoutConfig?.allCharacters || [];
    character = selectedChars.find(c => String(c.id) === String(currentCharacterId));
    if (!character?.id || character.voice_id === undefined) {
      character = await HangoutService.getInstance().getCharacterDetails(currentCharacterId);
    }
  } catch (err) {
    console.warn('Could not resolve character for sprite grid refresh:', err);
    return;
  }
  if (!character?.id) return;

  const newSection = await createInlineSpriteGrid(character, currentOutfitId);
  const form = panelContent.querySelector('.character-info-form');
  if (form && gridSection.parentNode === form) {
    form.replaceChild(newSection, gridSection);
  }
}

export async function openCharacterInfoModal() {
  const characterInfoPanel = document.getElementById('character-info-modal');
  if (characterInfoPanel) {
    const panelContent = characterInfoPanel.querySelector('.character-info-panel');
    if (panelContent) await refreshSpriteGridForCurrentOutfit(/** @type {HTMLElement} */ (panelContent));
    characterInfoPanel.classList.add('show');
    setTimeout(() => {
      const closeButton = /** @type {HTMLElement} */ (characterInfoPanel.querySelector('.close-modal'));
      closeButton?.focus();
    }, 100);
  } else {
    console.warn('Character info panel not found');
  }
}

export function closeCharacterInfoModal() {
  const characterInfoPanel = document.getElementById('character-info-modal');
  if (characterInfoPanel) {
    if (characterInfoPanel._keydownHandler) {
      document.removeEventListener('keydown', characterInfoPanel._keydownHandler);
    }
    if (characterInfoPanel._characterImageHandler) {
      document.removeEventListener('click', characterInfoPanel._characterImageHandler);
    }
    if (characterInfoPanel._mappingsHandler) {
      document.removeEventListener('mappingsUpdated', characterInfoPanel._mappingsHandler);
    }

    characterInfoPanel.classList.remove('show');
    setTimeout(() => {
      characterInfoPanel.parentNode?.removeChild(characterInfoPanel);
    }, 400);
  }
}
